/**
 * LuckyMenu design tokens. See docs/product/ux-design.md.
 * Colour names follow shadcn/ui so web components work unchanged. The brand's
 * marigold accent is `highlight`, because shadcn uses `accent` for hover fills.
 */

export const colorNames = [
	"background",
	"foreground",
	"card",
	"card-foreground",
	"popover",
	"popover-foreground",
	"surface",
	"primary",
	"primary-foreground",
	"primary-pressed",
	"secondary",
	"secondary-foreground",
	"muted",
	"muted-foreground",
	"accent",
	"accent-foreground",
	"highlight",
	"highlight-foreground",
	"success",
	"success-foreground",
	"warning",
	"warning-foreground",
	"destructive",
	"destructive-foreground",
	"border",
	"input",
	"ring",
	"veg",
	"nonveg",
	"egg",
] as const;

export type ColorName = (typeof colorNames)[number];
export type Palette = Record<ColorName, string>;

const INK = "#14171A";
const WHITE = "#FFFFFF";
const DARK_BG = "#0E1113";

export const light: Palette = {
	background: WHITE,
	foreground: INK,
	card: WHITE,
	"card-foreground": INK,
	popover: WHITE,
	"popover-foreground": INK,
	surface: "#F6F7F8",
	primary: "#0B7A4B",
	"primary-foreground": WHITE,
	"primary-pressed": "#075C38",
	secondary: "#EEF0F2",
	"secondary-foreground": INK,
	muted: "#F6F7F8",
	"muted-foreground": "#6B7280",
	accent: "#EEF0F2",
	"accent-foreground": INK,
	highlight: "#FFB020",
	"highlight-foreground": INK,
	success: "#15803D",
	"success-foreground": WHITE,
	warning: "#F59E0B",
	"warning-foreground": INK,
	destructive: "#DC2626",
	"destructive-foreground": WHITE,
	border: "#E3E6EA",
	input: "#D5D9DE",
	ring: "#0B7A4B",
	veg: "#0F8A3C",
	nonveg: "#B42318",
	egg: "#B77F00",
};

export const dark: Palette = {
	background: DARK_BG,
	foreground: "#ECEEF0",
	card: "#15191C",
	"card-foreground": "#ECEEF0",
	popover: "#15191C",
	"popover-foreground": "#ECEEF0",
	surface: "#171B1F",
	primary: "#34B27B",
	"primary-foreground": DARK_BG,
	"primary-pressed": "#2A9466",
	secondary: "#22282D",
	"secondary-foreground": "#ECEEF0",
	muted: "#1B2024",
	"muted-foreground": "#9BA3AF",
	accent: "#22282D",
	"accent-foreground": "#ECEEF0",
	highlight: "#FFB020",
	"highlight-foreground": INK,
	success: "#4ADE80",
	"success-foreground": DARK_BG,
	warning: "#F59E0B",
	"warning-foreground": DARK_BG,
	destructive: "#F87171",
	"destructive-foreground": DARK_BG,
	border: "#2A3036",
	input: "#343B42",
	ring: "#34B27B",
	veg: "#22C55E",
	nonveg: "#F87171",
	egg: "#FBBF24",
};

export const palettes = { light, dark } as const;
export type ThemeName = keyof typeof palettes;

/**
 * Every pair used for text: [text colour, background colour]. Each must reach
 * WCAG AA (4.5:1). Add a pair here before using it in a component.
 */
export const textPairs: readonly [ColorName, ColorName][] = [
	["foreground", "background"],
	["foreground", "surface"],
	["foreground", "muted"],
	["card-foreground", "card"],
	["popover-foreground", "popover"],
	["muted-foreground", "background"],
	["muted-foreground", "surface"],
	["muted-foreground", "muted"],
	["muted-foreground", "card"],
	["primary-foreground", "primary"],
	["primary-foreground", "primary-pressed"],
	["primary", "background"],
	["primary", "surface"],
	["primary", "card"],
	["secondary-foreground", "secondary"],
	["accent-foreground", "accent"],
	["highlight-foreground", "highlight"],
	["success-foreground", "success"],
	["success", "background"],
	["warning-foreground", "warning"],
	["destructive-foreground", "destructive"],
	["destructive", "background"],
	["destructive", "card"],
];

/** Marks and outlines that are not text must reach 3:1 (WCAG 1.4.11). */
export const graphicPairs: readonly [ColorName, ColorName][] = [
	["veg", "background"],
	["nonveg", "background"],
	["egg", "background"],
	["veg", "card"],
	["nonveg", "card"],
	["egg", "card"],
	["ring", "background"],
];

/** Font sizes in px: 12, 14, 16, 18, 20, 24, 30. Same steps as Tailwind xs to 3xl. */
export const fontSize = {
	xs: { size: 12, lineHeight: 16 },
	sm: { size: 14, lineHeight: 20 },
	base: { size: 16, lineHeight: 24 },
	lg: { size: 18, lineHeight: 28 },
	xl: { size: 20, lineHeight: 28 },
	"2xl": { size: 24, lineHeight: 32 },
	"3xl": { size: 30, lineHeight: 36 },
} as const;

export const fontFamily = {
	sans: [
		"Inter Variable",
		"Inter",
		"Noto Sans",
		"Noto Sans Devanagari",
		"Noto Sans Bengali",
		"system-ui",
		"sans-serif",
	],
} as const;

/** Spacing unit in px. Tailwind's 4 px scale is used as is. */
export const spacingUnit = 4;

/** Radii in px: cards 12, buttons 10, chips fully round. */
export const radius = {
	sm: 6,
	md: 10,
	lg: 12,
	xl: 12,
	button: 10,
	card: 12,
	chip: 999,
} as const;

/** The single soft shadow for sheets and floating bars. */
export const shadow = {
	float: "0 8px 24px rgba(20, 23, 26, 0.12), 0 2px 6px rgba(20, 23, 26, 0.08)",
} as const;

/** Motion: 150 to 250 ms, ease-out. */
export const motion = {
	fast: 150,
	base: 200,
	slow: 250,
	easing: "cubic-bezier(0.16, 1, 0.3, 1)",
} as const;

/** Minimum tap target in px. */
export const minTapTarget = 48;
